/**
 * @file ColorRefField.tsx
 * @description Цвет оформления ссылкой — «как у сайта» (нет ключа), любая из
 * 9 ролей темы или свой цвет (phase19.16, партия SB120, этап 2;
 * uiux-sb120-stage2 §2 «рамка карточек», «Свечение»; данные
 * db-design-sb120-stage2 §1 `siteColorRefSchema`).
 *
 * ```
 * Цвет рамки                                    ↺ авто: линия
 * [Авто: линия ✓] [Фон] [Карточки] … [Кнопки] … [⌖ Свой]
 * ```
 *
 * Обёртка над `ColorField` в режиме ролей текста: у него та же модель
 * «авто → роль → свой» и все 9 ролей (режим заливки ролей «Кнопки» не
 * знает). Контраст здесь не считается (`elementBg` нет) — у рамки свой порог
 * 3:1, его предупреждение рядом (card-shapes.ts).
 */

import { SITE_COLOR_ROLE_INFO, SITE_COLOR_ROLES, type SiteColorRef, type SiteColorRole, type SiteColors } from "@chaiberi/shared";

import { ColorField, type ColorTextValue } from "./ColorField";

/** Подписи 9 ролей — из реестра shared. */
const ROLE_LABELS = Object.fromEntries(SITE_COLOR_ROLES.map((role) => [role, SITE_COLOR_ROLE_INFO[role].label])) as Record<SiteColorRole, string>;

interface Props {
  label: string;
  /** Нет — «как у сайта» (ячейка `autoLabel`). */
  value: SiteColorRef | undefined;
  onChange: (value: SiteColorRef | undefined) => void;
  colors: SiteColors;
  /** Подпись первой ячейки: «Авто: линия», «Акцент». */
  autoLabel: string;
  /** Роли в ячейках (по умолчанию все 9). */
  roles?: readonly SiteColorRole[];
}

/** Ссылка на цвет → значение поля. */
function toField(value: SiteColorRef | undefined): ColorTextValue {
  if (!value) return { kind: "auto" };
  return value.kind === "role" ? { kind: "role", role: value.role } : { kind: "custom", color: value.color };
}

/** Значение поля → ссылка на цвет (авто — без ключа). */
function fromField(value: ColorTextValue): SiteColorRef | undefined {
  if (value.kind === "auto") return undefined;
  return value.kind === "role" ? { kind: "role", role: value.role } : { kind: "custom", color: value.color };
}

export function ColorRefField({ label, value, onChange, colors, autoLabel, roles = SITE_COLOR_ROLES }: Props) {
  return (
    <ColorField
      mode="text"
      label={label}
      value={toField(value)}
      onChange={(next) => onChange(fromField(next))}
      roles={roles}
      labels={ROLE_LABELS}
      colors={colors}
      autoLabel={autoLabel}
      inheritLabel={autoLabel.toLowerCase()}
      onReset={value ? () => onChange(undefined) : undefined}
    />
  );
}
